<script>
/**
 * @component ChronicConditionList
 * @description ABM de condiciones crónicas: listado, búsqueda, paginación, alta, edición y borrado.
 * @example <ChronicConditionList />
 */
import chronicConditionService from "@/services/chronic-condition";
import Spinner from "@/components/ui/Spinner";
import { mapGetters } from "vuex";

export default {
  name: "ChronicConditionList",
  components: { Spinner },
  data() {
    return {
      isLoading: false,
      searchFilter: "",
      conditions: [],
      total: 0,
      currentPage: 1,
      pageSize: 20,
      isDialogVisible: false,
      dialogMode: "create", // 'create' | 'edit'
      form: { code: "", name: "", description: "" },
      editingId: null,
      formErrors: {},
    };
  },
  computed: {
    ...mapGetters(["isAdmin"]),
    filteredConditions() {
      if (!this.searchFilter) return this.conditions;
      const q = this.searchFilter.toLowerCase();
      return this.conditions.filter(
        (c) =>
          c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)
      );
    },
  },
  created() {
    this.fetchConditions();
  },
  methods: {
    async fetchConditions() {
      this.isLoading = true;
      try {
        const params = {
          offset: (this.currentPage - 1) * this.pageSize,
          limit: this.pageSize,
          search: this.searchFilter,
        };
        const res = await chronicConditionService.query(params);
        this.conditions = res.docs;
        this.total = res.total;
      } catch (e) {
        this.$message.error("Error al cargar condiciones crónicas");
      } finally {
        this.isLoading = false;
      }
    },
    onPageChange(page) {
      this.currentPage = page;
      this.fetchConditions();
    },
    openDialog(mode = "create", condition = null) {
      this.dialogMode = mode;
      this.isDialogVisible = true;
      this.formErrors = {};
      if (mode === "edit" && condition) {
        this.editingId = condition._id;
        this.form = {
          code: condition.code,
          name: condition.name,
          description: condition.description,
        };
      } else {
        this.editingId = null;
        this.form = { code: "", name: "", description: "" };
      }
    },
    closeDialog() {
      this.isDialogVisible = false;
      this.editingId = null;
      this.form = { code: "", name: "", description: "" };
      this.formErrors = {};
    },
    async saveCondition() {
      this.formErrors = {};
      if (!this.form.code) this.formErrors.code = "Código requerido";
      if (!this.form.name) this.formErrors.name = "Nombre requerido";
      if (Object.keys(this.formErrors).length) return;
      try {
        if (this.dialogMode === "create") {
          await chronicConditionService.create(this.form);
          this.$message.success("Condición creada");
        } else {
          await chronicConditionService.update(this.editingId, this.form);
          this.$message.success("Condición actualizada");
        }
        this.closeDialog();
        this.fetchConditions();
      } catch (e) {
        this.$message.error("Error al guardar");
      }
    },
    async deleteCondition(condition) {
      try {
        await this.$confirm(
          `¿Eliminar la condición "${condition.name}" (${condition.code})?`,
          "Eliminar",
          {
            confirmButtonText: "Aceptar",
            cancelButtonText: "Cancelar",
            type: "warning",
          }
        );
        await chronicConditionService.remove(condition._id);
        this.$message.success("Condición eliminada");
        this.fetchConditions();
      } catch {
        // Cancelado
      }
    },
  },
};
</script>

<template lang="pug">
section
  header.headline
    .headline__actions
      el-input.search__input(prefix-icon="el-icon-search" v-model="searchFilter" placeholder="Buscar por código o nombre" :clearable="true" @input="fetchConditions")
      .button.button--blue(@click="openDialog('create')" v-if="isAdmin")
        micon(name="add_circle_outline").button__icon
        span.button__text Nueva Condición
  .box.box--with-subnav
    .box__content--stretch
      table
        thead
          tr
            th Código
            th Nombre
            th Descripción
            th Acciones
        tbody
          spinner(v-if="isLoading")
          tr(v-else v-for="condition in filteredConditions" :key="condition._id" @click="openDialog('edit', condition)")
            td {{ condition.code }}
            td {{ condition.name }}
            td {{ condition.description }}
            td.actions
              .actions-container(@click.stop)
                button.button.button--action(@click="openDialog('edit', condition)")
                  micon(name="edit")
                button.button.button--action(@click="deleteCondition(condition)" v-if="isAdmin")
                  micon(name="delete_forever")
      el-pagination(
        v-if="total > pageSize"
        layout="prev, pager, next"
        :total="total"
        :page-size="pageSize"
        :current-page="currentPage"
        @current-change="onPageChange"
      )
  el-dialog(:title="dialogMode === 'create' ? 'Nueva Condición Crónica' : 'Editar Condición Crónica'" :visible.sync="isDialogVisible" width="720px")
    el-form(label-position="left" label-width="140px" size="small")
      el-row(:gutter="16")
        el-col(:span="8")
          el-form-item(label="Código" required :error="formErrors.code")
            el-input(v-model="form.code" maxlength="20" placeholder="Ej: E11")
        el-col(:span="16")
          el-form-item(label="Nombre" required :error="formErrors.name")
            el-input(v-model="form.name" maxlength="100" placeholder="Ej: Diabetes tipo 2")
      el-form-item(label="Descripción")
        el-input(type="textarea" v-model="form.description" maxlength="300" rows="2" placeholder="Descripción de la condición crónica")
    template(#footer)
      el-button(@click="closeDialog") Cancelar
      el-button(type="primary" @click="saveCondition") Guardar
</template>

<style scoped>
.headline__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.search__input {
  width: 250px;
}
.button--blue {
  background: #409eff;
  color: #fff;
}
.button--blue:hover {
  background: #66b1ff;
}
.box__content--stretch {
  overflow-x: auto;
}
tbody td:hover {
  cursor: pointer;
}
</style>
